import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import { alpha, Button, Collapse, Drawer, Grid, IconButton, Stack, Typography, styled } from '@mui/material';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import type ViewProductStore from '../../AppData/_stores/ViewProductStore';
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import { getCanonicalLocale } from '../../Common/SEO/SeoLinks';
import { getSeoProductsName } from '../../Common/SEO/StructuredData';
import buildCreateUrl from '../../ProductCatalog/buildCreateUrl';
import type { ProductCatalogCollection } from '../../ProductCatalog/Collections';
import ProductCatalogProvider from '../../ProductCatalog/ProductCatalogProvider';
import ProductCatalogResults from '../../ProductCatalog/Results/ProductCatalogResults';
import ProductCatalogToolbar from '../../ProductCatalog/Toolbar/ProductCatalogToolbar';
import useProductCatalogCategories from '../../ProductCatalog/hooks/useProductCatalogCategories';
import useProductCatalogCollections from '../../ProductCatalog/hooks/useProductCatalogCollections';
import { PRODUCT_SEARCH_PARAM } from '../../ProductCatalog/hooks/useProductCatalogFiltering';
import type { ProductSearchOption } from '../../ProductCatalog/hooks/useProductCatalogSearchOptions';
import { GetProductLabData } from '../../UIData/_actions/DesignLabActions';
import { EditActiveProduct, EditState } from '../../UIData/_actions/DesignLabStoreActions';
import ProductSelectorButton from './ProductSelectorButton';
import ProductSidebar from './ProductSidebar';
import RecentlyDesignedProducts from './RecentlyDesignedProducts';

const ProductListWrap = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'auto',
  transition: 'transform 300ms, opacity 300ms',
  padding: theme.spacing(4, 4, 12, 0),

  '@media (max-width: 1510px), (max-height: 1070px)': {
    paddingTop: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2, 2, 24, 2),
  },

  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(1),
  },

  '&.hidden': {
    transform: 'translateX(100px)',
    opacity: 0,
  }
}));

const ProductList = styled(Grid)(({ theme }) => ({
  alignContent: 'flex-start',
  flex: '1 1 auto',
  marginBottom: 0,
  marginTop: 0,
}));

const ProductSelectorRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  minWidth: 0,

  [theme.breakpoints.down('md')]: {
    flex: '1 1 0',
    height: '100%',
  },
}));

const ProductSelectorContainer = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: '64px',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 100,
  display: 'none',
  padding: '0 0 0 30px',
  '&.opened': {
    display: 'flex'
  },
  '@media (max-width: 1510px), (max-height: 1070px)': {
    padding: '0 0 0 16px'
  },
  [theme.breakpoints.down('md')]: {
    padding: 0,
    top: 'calc(var(--design-lab-content-top, 74px) - 10px)',
  },
}));

interface ProductSelectorProps {
  previewVariantByProductId: Record<number, string>
  onPreviewVariantChange: (productId: number, variant: string) => void
  onSelectedViewProductChange: (viewProduct?: ViewProductStore) => void
}

type CategoryTone = ProductCatalogCollection['navTone'];

interface MobileCategoryBreadcrumbItem {
  categoryId?: number
  key: string
  label: string
}

const CategoryChip = styled(Button, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'subtle' && prop !== 'tone',
})<{ active?: boolean, subtle?: boolean, tone?: CategoryTone }>(({ active, subtle, theme, tone }) => {
  const toneColor = tone === 'bestseller'
    ? theme.palette.accent.main
    : tone === 'new'
      ? theme.palette.primary.main
      : undefined;
  const activeColor = toneColor ?? theme.palette.common.black;

  return {
    backgroundColor: active === true
      ? (toneColor === undefined ? theme.palette.common.black : alpha(activeColor, 0.1))
      : theme.palette.common.white,
    border: '1px solid',
    borderColor: active === true ? activeColor : alpha(theme.palette.common.black, 0.1),
    borderRadius: 999,
    color: active === true
      ? (toneColor === undefined ? theme.palette.common.white : activeColor)
      : toneColor ?? (subtle === true ? theme.palette.text.secondary : theme.palette.text.primary),
    flexShrink: 0,
    gap: theme.spacing(0.6),
    fontWeight: 700,
    minHeight: subtle === true ? 32 : 36,
    lineHeight: 1,
    padding: subtle === true ? theme.spacing(0.5, 1.35) : theme.spacing(0.75, 1.75),
    textTransform: 'none',
    whiteSpace: 'nowrap',

    '&:hover': {
      backgroundColor: active === true
        ? (toneColor === undefined ? theme.palette.common.black : alpha(activeColor, 0.14))
        : alpha(activeColor, toneColor === undefined ? 0.04 : 0.08),
      borderColor: active === true ? activeColor : alpha(activeColor, toneColor === undefined ? 0.2 : 0.34),
    },

    '& .product-selector-category-icon': {
      flexShrink: 0,
      fontSize: 18,
    },
  };
});

const DesktopCategoryControls = styled(Stack)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const MobileSelectedCategoryHeader = styled(Stack)(({ theme }) => ({
  display: 'none',

  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    padding: theme.spacing(0.75, 2, 1.5),
  },
}));

const MobileCategoryBreadcrumbNav = styled('nav')(({ theme }) => ({
  alignItems: 'center',
  color: 'rgba(0, 0, 0, 0.58)',
  display: 'flex',
  flexWrap: 'wrap',
  fontSize: 12,
  fontWeight: 700,
  gap: theme.spacing(0.5),
  lineHeight: 1.4,

  '& button': {
    appearance: 'none',
    background: 'transparent',
    border: 0,
    color: 'inherit',
    cursor: 'pointer',
    font: 'inherit',
    letterSpacing: 0,
    margin: 0,
    padding: 0,
    textAlign: 'left',
    textDecoration: 'none',
    textUnderlineOffset: 4,
    transition: 'color 160ms ease',
  },

  '& button:hover, & button:focus-visible': {
    color: 'rgba(0, 0, 0, 0.86)',
    outline: 'none',
    textDecoration: 'underline',
  },

  '& .product-selector-breadcrumb-separator': {
    color: 'rgba(0, 0, 0, 0.28)',
    userSelect: 'none',
  },
}));

const MobileSelectedCategoryTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontSize: '2rem',
  lineHeight: 1,
}));

const categoryDrawerPaperSx = {
  width: '100%',
  height: 'auto',
  maxHeight: 'min(78dvh, calc(100dvh - 52px))',
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
  backgroundColor: '#f0f0f0',
};

const CategoryDrawerButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'tone',
})<{ active?: boolean, tone?: CategoryTone }>(({ active, theme, tone }) => {
  const toneColor = tone === 'bestseller'
    ? theme.palette.accent.main
    : tone === 'new'
      ? theme.palette.primary.main
      : undefined;
  const activeColor = toneColor ?? theme.palette.secondary.main;

  return {
    backgroundColor: active === true ? alpha(activeColor, 0.08) : 'transparent',
    borderRadius: 8,
    color: toneColor ?? (active === true ? activeColor : theme.palette.text.primary),
    fontWeight: active === true ? 700 : 600,
    justifyContent: 'flex-start',
    minHeight: 44,
    padding: theme.spacing(0.85, 1.25),
    textTransform: 'none',

    '&:hover': {
      backgroundColor: active === true ? alpha(activeColor, 0.12) : alpha(activeColor, 0.05),
    },

    '& .product-selector-category-icon': {
      flexShrink: 0,
      fontSize: 18,
      marginRight: theme.spacing(1),
    },

    '& .product-selector-category-expand': {
      fontSize: 18,
      marginLeft: 'auto',
      transition: 'transform 150ms ease',
    },
  };
});

export default function ProductSelector({
  previewVariantByProductId,
  onPreviewVariantChange,
  onSelectedViewProductChange,
}: ProductSelectorProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [t] = useTranslation('design-lab');
  const [tProducts] = useTranslation('products');
  const [tR] = useTranslation('routes');
  const params = useParams<{ product?: string }>();
  const [searchParams] = useSearchParams();
  const productSearchQuery = searchParams.get(PRODUCT_SEARCH_PARAM) ?? '';
  const {
    categoriesById,
    childrenByParentId,
    topLevelCategories,
  } = useProductCatalogCategories();
  const products = useAppSelector(state => state.get('appData').get('products'));
  const viewProducts = useAppSelector(state => state.get('appData').get('viewProducts'));
  const userLocale = useAppSelector(state => state.get('userData').get('prefs').get('lang'));
  const currentLocale = getCanonicalLocale(userLocale);
  const activeProductId = useAppSelector(state => state.get('UIData').get('designLab').get('activeProductId'));
  const activeVariant = useAppSelector(state => state.get('UIData').get('designLab').get('activeVariant'));
  const productCatalogCollections = useProductCatalogCollections();

  const [opened, setOpened] = useState(!params.product);
  const [hide, setHide] = useState(!!params.product);
  const [pendingViewProductId, setPendingViewProductId] = useState<number | null>(null);
  const [selectedViewProductDismissed, setSelectedViewProductDismissed] = useState(false);
  const [selectedCollectionSlug, setSelectedCollectionSlug] = useState<string | null>(null);
  const [selectedParentCategoryId, setSelectedParentCategoryId] = useState<number | null>(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<number | null>(null);
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
  const [expandedMobileCategoryId, setExpandedMobileCategoryId] = useState<number | null>(null);
  const pendingCommitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!hide) {
      return;
    }

    const closeTimeout = setTimeout(() => {
      setOpened(false);

      if (pendingCommitTimeoutRef.current === null) {
        dispatch(EditState('lab'));
      }
    }, 300);

    return () => clearTimeout(closeTimeout);
  }, [dispatch, hide])

  useEffect(() => {
    if (opened) {
      dispatch(EditState('product-selector'));
      setTimeout(() => {
        setHide(false);
      }, 300);
    }
  }, [dispatch, opened]);

  useEffect(() => {
    if (opened || pendingViewProductId !== null) {
      return;
    }

    setSelectedViewProductDismissed(false);
  }, [opened, pendingViewProductId]);

  useEffect(() => {
    return () => {
      if (pendingCommitTimeoutRef.current !== null) {
        clearTimeout(pendingCommitTimeoutRef.current);
      }
    }
  }, []);

  const labCategoryIds = useMemo(() => {
    const categoryIds = new Set<number>();

    viewProducts.forEach(viewProduct => {
      const productId = viewProduct.get('id_product');
      if (productId === null || !products.get(String(productId))?.get('visible')) {
        return;
      }

      viewProduct.get('categories').forEach(categoryId => {
        const category = categoriesById.get(categoryId);
        if (!category) {
          return;
        }

        categoryIds.add(category.get('id'));

        const parentId = category.get('id_parent');
        if (parentId !== null) {
          categoryIds.add(parentId);
        }
      });
    });

    return categoryIds;
  }, [categoriesById, products, viewProducts])

  const labCategories = useMemo(() => {
    return topLevelCategories.filter(category => labCategoryIds.has(category.get('id')));
  }, [labCategoryIds, topLevelCategories])

  const selectedLabCollection = useMemo(() => {
    return selectedCollectionSlug === null
      ? undefined
      : productCatalogCollections.find(collection => collection.slug === selectedCollectionSlug);
  }, [productCatalogCollections, selectedCollectionSlug])

  const selectedParentCategory = useMemo(() => {
    return selectedParentCategoryId === null ? undefined : categoriesById.get(selectedParentCategoryId);
  }, [categoriesById, selectedParentCategoryId])

  const selectedSubCategory = useMemo(() => {
    return selectedSubCategoryId === null ? undefined : categoriesById.get(selectedSubCategoryId);
  }, [categoriesById, selectedSubCategoryId])

  const mobileSelectedCategoryTitle = useMemo(() => {
    if (selectedLabCollection !== undefined) {
      return selectedLabCollection.title(tProducts);
    }

    if (selectedSubCategory !== undefined) {
      return selectedSubCategory.get('name');
    }

    if (selectedParentCategory !== undefined) {
      return selectedParentCategory.get('name');
    }

    return tProducts('All Products');
  }, [selectedLabCollection, selectedParentCategory, selectedSubCategory, tProducts])

  const mobileCategoryBreadcrumbItems = useMemo<MobileCategoryBreadcrumbItem[]>(() => {
    const items: MobileCategoryBreadcrumbItem[] = [{
      key: 'products',
      label: getSeoProductsName(currentLocale),
    }];

    if (selectedSubCategory !== undefined && selectedParentCategory !== undefined) {
      items.push({
        categoryId: selectedParentCategory.get('id'),
        key: 'parent-' + selectedParentCategory.get('id'),
        label: selectedParentCategory.get('name'),
      });
    }

    return items;
  }, [currentLocale, selectedParentCategory, selectedSubCategory])

  const mobileCategoryBreadcrumbAriaLabel = useMemo(() => {
    return mobileCategoryBreadcrumbItems.map(item => item.label).join(' / ');
  }, [mobileCategoryBreadcrumbItems])

  const labSubCategories = useMemo(() => {
    if (selectedParentCategoryId === null) {
      return [];
    }

    return (childrenByParentId.get(selectedParentCategoryId) ?? []).filter(category => labCategoryIds.has(category.get('id')));
  }, [childrenByParentId, labCategoryIds, selectedParentCategoryId])

  const selectedCategoryIds = useMemo(() => {
    if (selectedSubCategoryId !== null) {
      return new Set([selectedSubCategoryId]);
    }

    if (selectedParentCategoryId === null) {
      return undefined;
    }

    const categoryIds = new Set([selectedParentCategoryId]);
    (childrenByParentId.get(selectedParentCategoryId) ?? []).forEach(category => {
      categoryIds.add(category.get('id'));
    });

    return categoryIds;
  }, [childrenByParentId, selectedParentCategoryId, selectedSubCategoryId])

  useEffect(() => {
    if (selectedParentCategoryId !== null && !labCategoryIds.has(selectedParentCategoryId)) {
      setSelectedParentCategoryId(null);
      setSelectedSubCategoryId(null);
    }

    if (selectedSubCategoryId !== null && !labCategoryIds.has(selectedSubCategoryId)) {
      setSelectedSubCategoryId(null);
    }
  }, [labCategoryIds, selectedParentCategoryId, selectedSubCategoryId])

  useEffect(() => {
    if (
      selectedCollectionSlug !== null
      && productCatalogCollections.every(collection => collection.slug !== selectedCollectionSlug)
    ) {
      setSelectedCollectionSlug(null);
    }
  }, [productCatalogCollections, selectedCollectionSlug])

  useEffect(() => {
    if (productSearchQuery.trim() === '') {
      return;
    }

    setSelectedCollectionSlug(null);
    setSelectedParentCategoryId(null);
    setSelectedSubCategoryId(null);
  }, [productSearchQuery])

  const labProductFilter = useCallback((viewProduct: ViewProductStore) => {
    if (viewProduct.get('id_product') === null) {
      return false;
    }

    if (!products.get(String(viewProduct.get('id_product')))?.get('visible')) {
      return false;
    }

    if (selectedLabCollection !== undefined && !selectedLabCollection.matchesProduct(viewProduct)) {
      return false;
    }

    return selectedCategoryIds === undefined
      || viewProduct.get('categories').some(categoryId => selectedCategoryIds.has(categoryId));
  }, [products, selectedCategoryIds, selectedLabCollection])

  const activeViewProduct = useMemo(() => {
    if (activeProductId === null) {
      return undefined;
    }

    const matchingViewProducts = viewProducts
      .valueSeq()
      .filter(viewProduct => {
        const productId = viewProduct.get('id_product');

        return productId !== null
          && Number(productId) === activeProductId
          && products.get(String(productId))?.get('visible');
      })
      .toArray();

    const fallbackViewProduct = matchingViewProducts.find(viewProduct => viewProduct.get('pricingOptions').count() === 0)
      ?? matchingViewProducts[0];

    const viewProductIdParam = Number(searchParams.get('viewProduct'));
    const viewProductFromParam = Number.isFinite(viewProductIdParam) && viewProductIdParam > 0
      ? viewProducts.get(String(viewProductIdParam))
      : undefined;

    if (viewProductFromParam?.get('id_product') === activeProductId) {
      return viewProductFromParam;
    }

    return fallbackViewProduct;
  }, [activeProductId, products, searchParams, viewProducts])

  const selectedViewProduct = useMemo(() => {
    if (pendingViewProductId === null) {
      return selectedViewProductDismissed ? undefined : activeViewProduct;
    }

    return viewProducts.get(String(pendingViewProductId)) ?? activeViewProduct;
  }, [activeViewProduct, pendingViewProductId, selectedViewProductDismissed, viewProducts])

  useEffect(() => {
    if (opened || pendingViewProductId === null) {
      return;
    }

    const routeViewProductId = Number(searchParams.get('viewProduct'));
    if (!Number.isFinite(routeViewProductId) || routeViewProductId !== pendingViewProductId) {
      return;
    }

    const pendingViewProduct = viewProducts.get(String(pendingViewProductId));
    if (pendingViewProduct?.get('id_product') !== activeProductId) {
      return;
    }

    setPendingViewProductId(null);
    setSelectedViewProductDismissed(false);
  }, [activeProductId, opened, pendingViewProductId, searchParams, viewProducts])

  useEffect(() => {
    onSelectedViewProductChange(selectedViewProduct);
  }, [onSelectedViewProductChange, selectedViewProduct])

  const onSelectViewProduct = useCallback((viewProduct: ViewProductStore) => {
    if (viewProduct.get('id_product') === null) {
      return;
    }

    const selectedProduct = products.get(String(viewProduct.get('id_product')));
    if (!selectedProduct) {
      return;
    }

    if (selectedProduct.get('labData') === undefined) {
      dispatch(GetProductLabData(selectedProduct.get('slug')));
    }

    setSelectedViewProductDismissed(false);
    setPendingViewProductId(viewProduct.get('id'));
  }, [dispatch, products])

  const onDeselectSelectedViewProduct = useCallback(() => {
    setPendingViewProductId(null);
    setSelectedViewProductDismissed(true);
  }, [])

  const commitSelectedViewProduct = useCallback((viewProduct = selectedViewProduct) => {
    const productId = viewProduct?.get('id_product');

    if (productId === undefined || productId === null) {
      setHide(true);
      return;
    }

    const createProduct = products.get(String(productId));
    if (!createProduct) {
      return;
    }

    const selectedVariantSlug = productId !== activeProductId
      ? previewVariantByProductId[productId]
      : activeVariant;
    const selectedVariant = createProduct.get('variants')
      .find(variant => variant.get('slug') === selectedVariantSlug)
      ?.get('slug');

    const createUrl = buildCreateUrl(
      viewProduct,
      createProduct,
      tR('/create'),
      {
        baseSearchParams: searchParams,
        variant: selectedVariant,
      }
    );
    const currentUrl = location.pathname + location.search;
    const shouldLoadProduct = createUrl !== currentUrl;

    setHide(true);

    if (shouldLoadProduct) {
      if (pendingCommitTimeoutRef.current !== null) {
        clearTimeout(pendingCommitTimeoutRef.current);
      }

      pendingCommitTimeoutRef.current = setTimeout(() => {
        dispatch(EditActiveProduct(null));
        dispatch(EditState('lab'));
        void navigate(createUrl, { replace: true });
        pendingCommitTimeoutRef.current = null;
      }, 300);
    }
  }, [activeProductId, activeVariant, dispatch, location.pathname, location.search, navigate, previewVariantByProductId, products, searchParams, selectedViewProduct, tR])

  const closeProductSelector = useCallback(() => {
    if (pendingViewProductId !== null) {
      commitSelectedViewProduct();
      return;
    }

    setHide(true);
  }, [commitSelectedViewProduct, pendingViewProductId])

  const getProductCatalogCardProps = useCallback((viewProduct: ViewProductStore) => {
    return {
      createUrlSearchParams: searchParams,
      onDoubleClick: commitSelectedViewProduct,
      onSelect: onSelectViewProduct,
      replaceCreateUrl: true,
      selected: selectedViewProduct?.get('id') === viewProduct.get('id'),
      selectOnly: true,
      variant: 'selector' as const,
    };
  }, [commitSelectedViewProduct, onSelectViewProduct, searchParams, selectedViewProduct])

  const onOpenButtonClick = useCallback(() => {
    if (opened) {
      closeProductSelector();
    } else {
      setOpened(true);
    }
  }, [closeProductSelector, opened])

  const onStart = useCallback(() => {
    closeProductSelector();
  }, [closeProductSelector])

  const onSelectAllProducts = useCallback(() => {
    setSelectedCollectionSlug(null);
    setSelectedParentCategoryId(null);
    setSelectedSubCategoryId(null);
    setCategoryDrawerOpen(false);
  }, [])

  const onSelectCollection = useCallback((collectionSlug: string) => {
    setSelectedCollectionSlug(collectionSlug);
    setSelectedParentCategoryId(null);
    setSelectedSubCategoryId(null);
    setCategoryDrawerOpen(false);
  }, [])

  const onSelectParentCategory = useCallback((categoryId: number, closeDrawer = false) => {
    setSelectedCollectionSlug(null);
    setSelectedParentCategoryId(categoryId);
    setSelectedSubCategoryId(null);

    if (closeDrawer) {
      setCategoryDrawerOpen(false);
    }
  }, [])

  const onSelectSubCategory = useCallback((categoryId: number, closeDrawer = false) => {
    setSelectedCollectionSlug(null);
    setSelectedSubCategoryId(categoryId);

    if (closeDrawer) {
      setCategoryDrawerOpen(false);
    }
  }, [])

  const onCategoryDrawerOpen = useCallback(() => {
    setExpandedMobileCategoryId(selectedParentCategoryId);
    setCategoryDrawerOpen(true);
  }, [selectedParentCategoryId])

  const onCategoryDrawerClose = useCallback(() => {
    setCategoryDrawerOpen(false);
  }, [])

  const filterLabSearchOption = useCallback((option: ProductSearchOption) => {
    if (option.type !== 'category') {
      return true;
    }

    const categoryId = Number(option.key.replace('category:', ''));
    return labCategoryIds.has(categoryId);
  }, [labCategoryIds])

  const onCategorySearchOptionSelect = useCallback((option: ProductSearchOption) => {
    if (option.type !== 'category') {
      return;
    }

    const categoryId = Number(option.key.replace('category:', ''));
    const category = categoriesById.get(categoryId);
    if (!category) {
      return;
    }

    const parentId = category.get('id_parent');
    if (parentId === null) {
      onSelectParentCategory(category.get('id'));
      return;
    }

    setSelectedCollectionSlug(null);
    setSelectedParentCategoryId(parentId);
    setSelectedSubCategoryId(category.get('id'));
  }, [categoriesById, onSelectParentCategory])

  const renderMobileCategoryBreadcrumbItem = useCallback((item: MobileCategoryBreadcrumbItem, index: number) => {
    const categoryId = item.categoryId;

    return <Fragment key={item.key}>
      {index > 0 ? <span className="product-selector-breadcrumb-separator" aria-hidden="true">/</span> : null}
      <button
        type="button"
        onClick={categoryId === undefined ? onSelectAllProducts : () => onSelectParentCategory(categoryId)}
      >
        {item.label}
      </button>
    </Fragment>
  }, [onSelectAllProducts, onSelectParentCategory])

  const renderCategoryChip = useCallback((options: {
    active: boolean
    icon?: ElementType<{ className?: string }>
    label: string
    key: string | number
    onClick: () => void
    subtle?: boolean
    tone?: CategoryTone
  }) => {
    const CategoryIcon = options.icon;

    return <CategoryChip
      key={options.key}
      active={options.active}
      aria-pressed={options.active}
      color="secondary"
      size="small"
      subtle={options.subtle}
      tone={options.tone}
      variant="outlined"
      onClick={options.onClick}
    >
      {CategoryIcon !== undefined ? <CategoryIcon className="product-selector-category-icon" /> : null}
      {options.label}
    </CategoryChip>
  }, [])

  const renderCategoryDrawerButton = useCallback((options: {
    active: boolean
    icon?: ElementType<{ className?: string }>
    label: string
    key: string | number
    onClick: () => void
    tone?: CategoryTone
  }) => {
    const CategoryIcon = options.icon;

    return <CategoryDrawerButton
      key={options.key}
      active={options.active}
      color="secondary"
      tone={options.tone}
      variant="text"
      onClick={options.onClick}
    >
      {CategoryIcon !== undefined ? <CategoryIcon className="product-selector-category-icon" /> : null}
      {options.label}
    </CategoryDrawerButton>
  }, [])

  const renderMobileParentCategory = useCallback((category: typeof labCategories[number]) => {
    const childCategories = (childrenByParentId.get(category.get('id')) ?? []).filter(childCategory => labCategoryIds.has(childCategory.get('id')));
    const expanded = expandedMobileCategoryId === category.get('id');
    const selected = selectedParentCategoryId === category.get('id');

    if (childCategories.length === 0) {
      return renderCategoryDrawerButton({
        active: selected,
        key: category.get('id'),
        label: category.get('name'),
        onClick: () => onSelectParentCategory(category.get('id'), true),
      });
    }

    return <Stack key={category.get('id')} spacing={0.25}>
      <CategoryDrawerButton
        active={selected}
        color="secondary"
        variant="text"
        onClick={() => setExpandedMobileCategoryId(value => value === category.get('id') ? null : category.get('id'))}
      >
        {category.get('name')}
        <ExpandMoreRoundedIcon
          className="product-selector-category-expand"
          sx={{
            transform: expanded ? 'rotate(180deg)' : undefined,
          }}
        />
      </CategoryDrawerButton>
      <Collapse in={expanded} timeout={150} unmountOnExit>
        <Stack spacing={0.25} sx={{ pl: 2 }}>
          {renderCategoryDrawerButton({
            active: selected && selectedSubCategoryId === null,
            key: 'view-all-' + category.get('id'),
            label: tProducts('View all in {{category}}', { category: category.get('name') }),
            onClick: () => onSelectParentCategory(category.get('id'), true),
          })}
          {childCategories.map(childCategory => renderCategoryDrawerButton({
            active: selectedSubCategoryId === childCategory.get('id'),
            key: childCategory.get('id'),
            label: childCategory.get('name'),
            onClick: () => {
              setSelectedParentCategoryId(category.get('id'));
              onSelectSubCategory(childCategory.get('id'), true);
            },
          }))}
        </Stack>
      </Collapse>
    </Stack>
  }, [childrenByParentId, expandedMobileCategoryId, labCategoryIds, onSelectParentCategory, onSelectSubCategory, renderCategoryDrawerButton, selectedParentCategoryId, selectedSubCategoryId, tProducts])

  return <ProductSelectorRoot>
    <ProductSelectorButton
      opened={opened}
      selectedViewProduct={selectedViewProduct}
      onClick={onOpenButtonClick}
    />
    <ProductSelectorContainer className={opened ? 'opened' : ''}>
      <ProductSidebar
        onStart={onStart}
        onDeselectSelectedProduct={onDeselectSelectedViewProduct}
        hidden={hide}
        previewVariantByProductId={previewVariantByProductId}
        selectedViewProduct={selectedViewProduct}
        onPreviewVariantChange={onPreviewVariantChange}
      />
      <ProductCatalogProvider productFilter={labProductFilter}>
        <ProductListWrap
          className={hide ? 'hidden' : ''}
        >
          <ProductList
            container
            spacing={{ xs: 1, md: 2, xl: 3 }}
            alignItems="stretch"
          >
            <RecentlyDesignedProducts
              enabled={opened && !hide}
              onOpenProduct={commitSelectedViewProduct}
              onSelectProduct={onSelectViewProduct}
              selectedViewProductId={selectedViewProduct?.get('id')}
            />
            <ProductCatalogToolbar
              includeBrowse
              includeBrowseDrawer={false}
              includeCategorySearchOptions
              onBrowseOpen={onCategoryDrawerOpen}
              onCategorySearchOptionSelect={onCategorySearchOptionSelect}
              productSearchOptionFilter={filterLabSearchOption}
              resetProductSearchToAllProducts={false}
            />
            <Grid item xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Stack spacing={1} sx={{ width: '100%' }}>
                <DesktopCategoryControls
                  spacing={1}
                >
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.75,
                      width: '100%',
                    }}
                  >
                    {renderCategoryChip({
                      active: selectedCollectionSlug === null && selectedParentCategoryId === null,
                      icon: GridViewRoundedIcon,
                      key: 'all',
                      label: tProducts('All Products'),
                      onClick: onSelectAllProducts,
                    })}
                    {productCatalogCollections.map(collection => renderCategoryChip({
                      active: selectedCollectionSlug === collection.slug,
                      icon: collection.navIcon,
                      key: collection.slug,
                      label: collection.title(tProducts),
                      onClick: () => onSelectCollection(collection.slug),
                      tone: collection.navTone,
                    }))}
                    {labCategories.map(category => renderCategoryChip({
                      active: selectedParentCategoryId === category.get('id'),
                      key: category.get('id'),
                      label: category.get('name'),
                      onClick: () => onSelectParentCategory(category.get('id')),
                    }))}
                  </Stack>
                  {selectedParentCategory !== undefined && labSubCategories.length > 0 ? <Stack
                    spacing={0.75}
                    sx={theme => ({
                      borderTop: '1px solid ' + alpha(theme.palette.common.black, 0.08),
                      pt: 1,
                      width: '100%',
                    })}
                  >
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                      {selectedParentCategory.get('name')}
                    </Typography>
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: {
                          xs: 0.5,
                          sm: 0.75,
                        },
                        width: '100%',
                      }}
                    >
                      {renderCategoryChip({
                        active: selectedSubCategoryId === null,
                        key: 'sub-all',
                        label: t('All'),
                        onClick: () => setSelectedSubCategoryId(null),
                        subtle: true,
                      })}
                      {labSubCategories.map(category => renderCategoryChip({
                        active: selectedSubCategoryId === category.get('id'),
                        key: 'sub-' + category.get('id'),
                        label: category.get('name'),
                        onClick: () => setSelectedSubCategoryId(category.get('id')),
                        subtle: true,
                      }))}
                    </Stack>
                  </Stack> : null}
                </DesktopCategoryControls>
              </Stack>
            </Grid>
            <Grid item xs={12} sx={{ display: { xs: 'block', sm: 'none' } }}>
              <MobileSelectedCategoryHeader spacing={0.5}>
                {mobileCategoryBreadcrumbItems.length > 0 ? <MobileCategoryBreadcrumbNav aria-label={mobileCategoryBreadcrumbAriaLabel}>
                  {mobileCategoryBreadcrumbItems.map(renderMobileCategoryBreadcrumbItem)}
                </MobileCategoryBreadcrumbNav> : null}
                <MobileSelectedCategoryTitle variant="h2">
                  {mobileSelectedCategoryTitle}
                </MobileSelectedCategoryTitle>
              </MobileSelectedCategoryHeader>
            </Grid>
            <ProductCatalogResults
              getCardProps={getProductCatalogCardProps}
              layout="lab"
            />
          </ProductList>
        </ProductListWrap>
      </ProductCatalogProvider>
    </ProductSelectorContainer>
    <Drawer
      anchor="bottom"
      open={categoryDrawerOpen}
      onClose={onCategoryDrawerClose}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: 1290,
        },
      }}
      PaperProps={{
        sx: categoryDrawerPaperSx,
      }}
    >
      <Stack sx={{ maxHeight: 'inherit' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 3,
            py: 2,
            borderBottom: '0.7px solid #afafaf',
          }}
        >
          <Typography variant="h3">{tProducts('Categories')}</Typography>
          <IconButton
            aria-label={tProducts('Hide Browse')}
            edge="end"
            onClick={onCategoryDrawerClose}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
        <Stack
          spacing={0.5}
          sx={{
            overflowY: 'auto',
            px: 3,
            py: 3,
          }}
        >
          {renderCategoryDrawerButton({
            active: selectedCollectionSlug === null && selectedParentCategoryId === null,
            icon: GridViewRoundedIcon,
            key: 'drawer-all',
            label: tProducts('All Products'),
            onClick: onSelectAllProducts,
          })}
          {productCatalogCollections.map(collection => renderCategoryDrawerButton({
            active: selectedCollectionSlug === collection.slug,
            icon: collection.navIcon,
            key: 'drawer-' + collection.slug,
            label: collection.title(tProducts),
            onClick: () => onSelectCollection(collection.slug),
            tone: collection.navTone,
          }))}
          {labCategories.map(renderMobileParentCategory)}
        </Stack>
      </Stack>
    </Drawer>
  </ProductSelectorRoot>
}
